import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { stripeAPI } from '../../helpers/api_service';
import { APP_URLS, COMPANY_BRANDING } from '../../helpers/url_helper';
import './StripePayment.css';

// Import checkmark icon for features (same as subscription page)
import Checkmark from '../../assets/checkmark.svg';

function CheckoutSession() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRenewal, setAutoRenewal] = useState(true); // Default to true
  
  // Get plan details from navigation state
  const planDetails = location.state;

  // Initialize theme on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Redirect if no plan details
  useEffect(() => {
    if (!planDetails) {
      navigate(APP_URLS.SUBSCRIPTION);
    }
  }, [planDetails, navigate]);

  const handleProcessPayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current theme from localStorage or context
      const currentTheme = mode; // 'dark' or 'light'
      
      // Get theme-based brand color
      const brandColor = currentTheme === 'dark' ? COMPANY_BRANDING.BRAND_COLORS.DARK : COMPANY_BRANDING.BRAND_COLORS.LIGHT;
      
      // Prepare checkout session data
      const checkoutData = {
        plan_id: planDetails.planId,
        billing_cycle: planDetails.billingCycle,
        customer_email: planDetails.customerEmail,
        auto_renewal: autoRenewal,
        logo_url: COMPANY_BRANDING.LOGO_URL,
        brand_color: brandColor,
        success_url: `${window.location.origin}${APP_URLS.PAYMENT_SUCCESS}?plan=${encodeURIComponent(planDetails.planTitle)}&billing=${planDetails.billingCycle}&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}${APP_URLS.PAYMENT_CANCEL}?plan=${encodeURIComponent(planDetails.planTitle)}&billing=${planDetails.billingCycle}`
      };

      // Create checkout session
      const response = await stripeAPI.createCheckoutSession(checkoutData);

      if (response.status === 'success' && response.data?.checkout_url) {
        // Store session_id from API response for backup use
        if (response.data.checkout_session_id) {
          localStorage.setItem('pendingStripeSessionId', response.data.checkout_session_id);
        }
        
        // Redirect to Stripe's hosted checkout page
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(response.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while processing your request');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(APP_URLS.SUBSCRIPTION);
  };

  if (!planDetails) {
    return null; // Will redirect in useEffect
  }
  return (
    <div className="checkout-session-page">
      <div className="checkout-two-column-layout">
        {/* Left Side - Plan Details & Features */}
        <div className="checkout-left-column">
          <div className="plan-details-section">
            {/* Header */}
            <div className="checkout-header">
              <h2 className="checkout-title">Complete Your Subscription</h2>
              <p className="checkout-subtitle">
                You're about to subscribe to our {planDetails.planTitle} plan
              </p>
            </div>

            {/* Selected Plan Features */}
            <div className="features-preview">
              <h5 className="features-title">Plan Features:</h5>
              <ul className="plan-features">
                {planDetails.formattedDescription && planDetails.formattedDescription.trim() ? (
                  planDetails.formattedDescription.split('\n').map((line, index) => {
                    // Skip the first line as it's the title/description
                    if (index === 0) return null;
                    
                    // Skip empty lines
                    if (!line.trim()) return null;
                    
                    return (
                      <li key={index} className="feature-item">
                        <img src={Checkmark} className="feature-check" alt="check" />
                        <span>{line.trim()}</span>
                      </li>
                    );
                  }).filter(Boolean)
                ) : (
                  // Show message if no features found
                  <li className="feature-item">
                    <img src={Checkmark} className="feature-check" alt="check" />
                    <span>Plan features will be displayed here</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="checkout-divider"></div>

        {/* Right Side - Subscription Summary & Payment */}
        <div className="checkout-right-column">
          <div className="payment-summary-section">
            {/* Plan Summary */}
            <div className="plan-summary">
              <h4 className="summary-title">Subscription Summary</h4>
              
              <div className="summary-item">
                <span className="summary-label">Plan:</span>
                <span className="summary-value">{planDetails.planTitle}</span>
              </div>
              
              <div className="summary-item">
                <span className="summary-label">Billing Cycle:</span>
                <span className="summary-value">
                  {planDetails.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </span>
              </div>
              
              <div className="summary-item">
                <span className="summary-label">Price:</span>
                <span className="summary-value price-value">
                  {planDetails.displayPrice}
                </span>
              </div>

            </div>

            {/* Error Display */}
            {error && (
              <div className="error-message">
                <p className="error-text">{error}</p>
              </div>
            )}

            {/* Auto Renewal Checkbox */}
            <div className="auto-renewal-section">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="autoRenewalCheckbox"
                  checked={autoRenewal}
                  onChange={(e) => setAutoRenewal(e.target.checked)}
                />
                <label htmlFor="autoRenewalCheckbox" className="form-check-label">
                  <strong>Enable Auto-Renewal</strong>
                  <div className="auto-renewal-description">
                    Automatically renew this subscription when it expires. You can cancel anytime from your account settings.
                  </div>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="checkout-actions">
              <Button 
                variant="primary" 
                className="action-btn primary-action"
                onClick={handleProcessPayment}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processing...
                  </>
                ) : (
                  'Proceed to Payment'
                )}
              </Button>
              
              <Button 
                variant="outline-secondary" 
                className="action-btn secondary-action"
                onClick={handleGoBack}
                disabled={loading}
              >
                Go Back
              </Button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSession;
